import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import styles from './ResultEditPopup.module.css';

import { deleteResults, saveRelayRunners } from 'api/resultApi';
import { getClubTeams } from 'api/club/teamApi';
import { editColumnPrimaryField, editRelayEventColumns } from 'config/admin/result';
import { clubFeaturesOf, clubForId, organisationWordOf } from 'utils/clubFeatures';
import { groupingBasisOf } from 'utils/groupLabel';
import { teamWordsOf } from 'utils/teamLabel';
import {
  OTHER_TEAM,
  hasRunnersButton,
  houseChoices,
  isRelayEvent,
  relayLegCount,
  runnersButtonLabel,
} from 'utils/relayTeams';
import { convertMillisecondsToRecord } from 'utils/time';
import { getErrorDetails } from 'utils/api';
import BackIcon from 'assets/images/icon_arrow_right.svg';
import CloseIcon from 'assets/images/icon_close.svg';
import AdminDataTable from 'components/admin/AdminDataTable';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import ReplayDelayNote from 'components/admin/ReplayDelayNote';
import RelayRunnersPopup from 'components/admin/RelayRunnersPopup';
import { getMeetAthletes } from 'api/meetRegistrationApi';
import { ATTEMPT_MAX_COUNT, fieldEvents } from 'config/admin/event';
import {
  getColumnsWithAthleteListOption,
  getFieldEventResultsWithNewAttempt,
  getTableData,
  getNewColumnTempData,
  deleteConfirmPopupUi,
  deleteResultPopupUi,
  getfieldEventResultFinalAttemptsForDelete,
  getHighJumpEventResultsWithNewAttempt,
} from './ResultEditPopupUi.helper';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm/CustomNewConfirm';
import {
  saveFieldEventResults,
  saveHighJumpEventResults,
  saveTrackEventResults,
  getChangedFieldEventResultAttempts,
  getChangedHighJumpEventResultAttempts,
  validateHighJumpHeightResult,
  validateFieldEventAttempt,
  validateTrackEventResult,
} from './ResultEditPopupApi.helper';

const ResultEditPopup = ({
  results = [],
  clubID,
  meetID,
  eventID,
  eventType,
  title,
  subtitle,
  showPopup,
  closePopup,
  refreshResults,
  onRunnersSaved,
  preliminary,
  roundType,
}) => {
  const isFieldEvent = fieldEvents.includes(eventType);
  const isHighJump = eventType === 'High Jump';
  // A relay is run by teams (utils/relayTeams.js): a school's houses and ad hoc
  // teams, or a club's typed teams. The club is the one the dialog is for, from
  // the club list already in memory (no request).
  const isRelay = isRelayEvent(eventType);
  const club = useSelector((state) => clubForId(state.user, clubID));
  const hasTeams = clubFeaturesOf(club).teams;
  const teamWords = teamWordsOf(club);
  // A school's houses, asked for once when a relay's dialog first opens
  // (served from the server's cache); null until then.
  const [houses, setHouses] = useState(null);
  // The saved team whose Runners dialog is open (step 3 file, section 13).
  const [runnersFor, setRunnersFor] = useState(null);
  const [isSavingRunners, setIsSavingRunners] = useState(false);
  const groupingBasis = groupingBasisOf(club);
  const [tableData, setTableData] = useState([]);
  const [originalTableData, setOriginalTableData] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmingResult, setConfirmingResult] = useState(null);
  const [editColumns, setEditColumns] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmPopup, setDeleteConfirmPopup] = useState(
    deleteConfirmPopupUi.result
  );
  const [editingMode, setEditingMode] = useState('VIEW');

  const EDITING_MODES = {
    VIEW: {
      canAddAthlete: true,
      canAddAttempt: true,
      canAddHeight: true,
      message: null,
    },
    ADD_ATHLETE: {
      canAddAthlete: false,
      canAddAttempt: false,
      canAddHeight: false,
      message: 'Save the new athlete first',
    },
    ADD_ATTEMPT: {
      canAddAthlete: false,
      canAddAttempt: false,
      canAddHeight: false,
      message: 'Save the new attempt first',
    },
    ADD_HEIGHT: {
      canAddAthlete: false,
      canAddAttempt: false,
      canAddHeight: false,
      message: 'Save the new height first',
    },
    EDIT_EXISTING: {
      canAddAthlete: false,
      canAddAttempt: false,
      canAddHeight: false,
      message: 'Save your changes first',
    },
  };

  // get all the athletes in this meet
  const fetchAthletes = useCallback(async () => {
  const res = await getMeetAthletes(meetID);
  if (res.data) {
    // Filter to only show active, participating athletes
    const activeAthletes = res.data.filter(
      athlete => athlete.isDeleted === 0 && athlete.participationStatus === 'YES'
    );
    setAthletes(activeAthletes);
  }
}, [meetID]);

  useEffect(() => {
    if (!showPopup || !isRelay || !hasTeams || houses !== null) return;
    getClubTeams(clubID)
      .then((response) => setHouses(response.data || []))
      .catch((error) => {
        console.error('[ResultEditPopup] Fetch houses error: ', error);
        toast.error(`The ${teamWords.lower.teams} could not be loaded.`);
      });
  }, [showPopup, isRelay, hasTeams, houses, clubID, teamWords.lower.teams]);

  useEffect(() => {
    setEditColumns(
      isRelay
        ? editRelayEventColumns({
            teamWord: hasTeams ? teamWords.team : 'Team',
            teamChoices: hasTeams ? houseChoices(houses) : null,
            runnersAction: (row) =>
              hasRunnersButton(row) ? (
                <CustomNewButton
                  label={runnersButtonLabel(row.runners)}
                  variant="outline"
                  customStyle={{ height: 28, padding: '0 10px', fontSize: 12 }}
                  onClick={() => openRunners(row)}
                />
              ) : null,
          })
        : getColumnsWithAthleteListOption(tableData, athletes, eventType)
    );
    // openRunners is made again on every render; what it reads (the rows and
    // the athletes) is in this list, so the button always calls a current one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, eventType, athletes, isRelay, hasTeams, houses, teamWords.team]);

  // A saved team's Runners button. The meet's athletes are asked for when the
  // first Runners dialog opens, and kept while the page is open, as Add
  // Athlete keeps them. Changes to the rows are saved first, as for a delete.
  const openRunners = async (row) => {
    if (hasUnsavedTrackEventChanges()) {
      toast.warning('Please save all changes before adding runners.');
      return;
    }
    if (athletes.length === 0) {
      try {
        await fetchAthletes();
      } catch (error) {
        console.error('[ResultEditPopup] Fetch athletes error: ', error);
        toast.error('The athletes could not be loaded.');
        return;
      }
    }
    setRunnersFor(row);
  };

  // Saves a team's runners: one request. The row then takes the runners the
  // server answered, with no reload (onRunnersSaved), or the event is fetched
  // again where that is not given.
  const saveRunners = async (runners) => {
    const resultID = runnersFor?.resultID;
    if (!resultID) return;
    try {
      setIsSavingRunners(true);
      const saved = await saveRelayRunners(resultID, runners);
      toast.success(runners.length ? 'Runners saved.' : 'Runners cleared.');
      setRunnersFor(null);
      if (onRunnersSaved) {
        onRunnersSaved(resultID, saved || []);
      } else if (refreshResults) {
        await refreshResults();
      }
    } catch (err) {
      console.error('[ResultEditPopup] Save runners error: ', err);
      toast.error(
        typeof err?.response?.data === 'string'
          ? err.response.data
          : 'The runners could not be saved.'
      );
    } finally {
      setIsSavingRunners(false);
    }
  };

  // A choice in a new relay team's cell: a house, Other team, or a typed name.
  // A house's team is named as the house, with no letter: two teams of a house
  // are told apart by their race and lane (Doug, 2026-10-17; step 3 file, 12.13).
  const applyTeamChoice = (rows, rowId, choice) =>
    rows.map((row) => {
      if (row[editColumnPrimaryField] !== rowId) return row;
      if (choice.kind === 'house') {
        const name =
          (houses || []).find((house) => house.teamID === choice.teamID)?.name || '';
        return {
          ...row,
          teamID: choice.teamID,
          teamChoice: choice.teamID,
          typedTeamName: '',
          athleteName: name,
          athleteFirstName: name,
        };
      }
      if (choice.kind === 'other') {
        return { ...row, teamID: null, teamChoice: OTHER_TEAM, typedTeamName: '', athleteName: '', athleteFirstName: '' };
      }
      const name = choice.name || '';
      return { ...row, teamID: null, typedTeamName: name, athleteName: name, athleteFirstName: name };
    });

  const hasUnsavedTrackEventChanges = () => {
    if (fieldEvents.includes(eventType) || eventType === 'High Jump') {
      return false;
    }

    const changedResults = getChangedTrackEventResults(
      tableData,
      originalTableData
    );
    const hasNewAthletes = tableData.some((r) => r.isNew);

    return changedResults.length > 0 || hasNewAthletes;
  };

  // Also add this helper function for getting changed track results
  const getChangedTrackEventResults = (tableData, originalTableData) => {
    return tableData.filter((row) => {
      if (row.isNew) return false;
      const original = originalTableData.find(
        (orig) => orig.resultID === row.resultID
      );
      if (!original) return false;

      // Compare laneNumber, resultValue, and status
      return (
        row.laneNumber !== original.laneNumber ||
        row.resultValue !== original.resultValue ||
        row.status !== original.status
      );
    });
  };

  const hasUnsavedFieldEventChanges = () => {
    if (!fieldEvents.includes(eventType)) {
      return false;
    }

    const changedAttempts = getChangedFieldEventResultAttempts(
      tableData,
      originalTableData
    );
    const hasNewAthletes = tableData.some((r) => r.isNew);
    const hasNewAttempts = tableData.some((result) => {
      for (let i = 1; i <= ATTEMPT_MAX_COUNT; i++) {
        if (result[`attempt${i}`]?.isNew) return true;
      }
      return false;
    });

    return changedAttempts.length > 0 || hasNewAthletes || hasNewAttempts;
  };

  const hasUnsavedHighJumpChanges = () => {
    if (eventType !== 'High Jump') {
      return false;
    }

    const changedAttempts = getChangedHighJumpEventResultAttempts(
      tableData,
      originalTableData
    );
    const hasNewAthletes = tableData.some((r) => r.isNew);
    const hasNewHeightAttempts = tableData.some((result) => {
      return result.heightAttempts?.some((attempt) => attempt.isNew);
    });

    return changedAttempts.length > 0 || hasNewAthletes || hasNewHeightAttempts;
  };

  // MAIN HANDLER FOR VALUE CHANGES IN THE TABLE
  // calls event-specific handlers
  const handleValueChange = (
    rowId,
    columnKey,
    newValue,
    type,
    isHeightChange
  ) => {
    if (isRelay && columnKey === 'athleteName' && newValue?.kind) {
      setTableData((prevData) => applyTeamChoice(prevData, rowId, newValue));
      return;
    }
    setTableData((prevData) =>
      prevData.map((row) => {
        if (isHeightChange || row[editColumnPrimaryField] === rowId) {
          // Determine event type and delegate to appropriate handler
          if (eventType === 'High Jump') {
            return handleHighJumpValueChange(row, columnKey, newValue, type);
          } else if (fieldEvents.includes(eventType)) {
            return handleFieldEventValueChange(row, columnKey, newValue, type);
          } else {
            return handleTrackEventValueChange(row, columnKey, newValue, type);
          }
        }
        return row;
      })
    );
  };

  // HIGH JUMP EVENT HANDLER
  const handleHighJumpValueChange = (row, columnKey, newValue, type) => {
    // Handle athlete selection for new rows
    if (row.isNew && columnKey === 'athleteName') {
      if (typeof newValue === 'object' && newValue.athleteID) {
        return {
          ...row,
          athleteID: newValue.athleteID,
          athleteAge: newValue.ageGroup,
          athleteGender: newValue.gender,
          athleteFirstName: newValue.firstName,
          athleteLastName: newValue.lastName,
          athleteName: `${newValue.firstName} ${newValue.lastName}`,
          athleteNumber: newValue.clubAthleteNumber,
        };
      }
      return { ...row, [columnKey]: newValue };
    }

    // Handle ALL height attempt changes (heights AND attempts)
    if (columnKey.startsWith('heightAttempts') && type) {
      const attemptIndex = parseInt(
        columnKey.replace('heightAttempts', ''),
        10
      );
      const attempts = Array.isArray(row.heightAttempts)
        ? [...row.heightAttempts]
        : [];

      // Ensure slot exists
      while (attempts.length <= attemptIndex) {
        const h = 0.5 + attempts.length * 0.1;
        attempts.push({
          height: parseFloat(h.toFixed(2)),
          attempt1: '.',
          attempt2: '.',
          attempt3: '.',
          resultId: `new-${Date.now()}-${attempts.length}`,
          isNew: row.isNew || false,
        });
      }

      // Handle height changes with validation
      if (type === 'height') {
        const newHeight = parseFloat(newValue);
        const prev = attempts[attemptIndex - 1];
        const next = attempts[attemptIndex + 1];

        // 1) Range check — toast but NO return
        if (isNaN(newHeight) || newHeight < 0.5 || newHeight > 2.5) {
          const toastId = 'height-range-error';
          if (!toast.isActive(toastId)) {
            toast.error('Height must be between 0.50 m and 2.50 m', {
              toastId,
            });
          }
        }

        // 2) Prev check — toast but NO return
        if (prev && newHeight <= prev.height) {
          const toastId = 'height-prev-error';
          if (!toast.isActive(toastId)) {
            toast.error(`Height must be > ${prev.height.toFixed(2)} m`, {
              toastId,
            });
          }
        }

        // 3) Next check — toast but NO return
        if (next && newHeight >= next.height) {
          const toastId = 'height-next-error';
          if (!toast.isActive(toastId)) {
            toast.error(`Height must be < ${next.height.toFixed(2)} m`, {
              toastId,
            });
          }
        }

        // **Always** update the model with the attempted value
        attempts[attemptIndex] = {
          ...attempts[attemptIndex],
          height: newHeight,
        };
      } else {
        // Handle attempt changes (attempt1, attempt2, attempt3)
        attempts[attemptIndex] = {
          ...attempts[attemptIndex],
          [type]: newValue,
        };
      }

      const updatedRow = {
        ...row,
        heightAttempts: attempts,
        [columnKey]: attempts[attemptIndex],
      };

      // Validate 3-attempt combination (for attempt changes, check if all attempts are defined)
      const currentAttempt = attempts[attemptIndex];
      if (type !== 'height') {
        // For attempt changes, validate if all three attempts are defined
        if (
          currentAttempt.attempt1 !== undefined &&
          currentAttempt.attempt2 !== undefined &&
          currentAttempt.attempt3 !== undefined
        ) {
          const validation = validateHighJumpHeightResult(
            currentAttempt.attempt1,
            currentAttempt.attempt2,
            currentAttempt.attempt3,
            false
          );

          if (!validation.isValid) {
            const toastId = 'height-result-error';
            if (!toast.isActive(toastId)) {
              toast.error(
                `Height ${currentAttempt.height.toFixed(2)} m: ${validation.error}`,
                { toastId }
              );
            }
          }
        }
      } else {
        // For height changes, always validate the existing attempts
        const { attempt1, attempt2, attempt3, height } = attempts[attemptIndex];
        const validation = validateHighJumpHeightResult(
          attempt1,
          attempt2,
          attempt3,
          false
        );
        if (!validation.isValid) {
          const toastId = 'height-result-error';
          if (!toast.isActive(toastId)) {
            toast.error(`Height ${height.toFixed(2)} m: ${validation.error}`, {
              toastId,
            });
          }
        }
      }

      return updatedRow;
    }

    // Handle other high jump properties
    return {
      ...row,
      [columnKey]: !type ? newValue : { ...row[columnKey], [type]: newValue },
    };
  };

  // FIELD EVENT HANDLER (Shot Put, Discus, etc.)
  const handleFieldEventValueChange = (row, columnKey, newValue, type) => {
    // Handle athlete selection for new rows
    if (row.isNew && columnKey === 'athleteName') {
      if (typeof newValue === 'object' && newValue.athleteID) {
        return {
          ...row,
          athleteID: newValue.athleteID,
          athleteAge: newValue.ageGroup,
          athleteGender: newValue.gender,
          athleteFirstName: newValue.firstName,
          athleteLastName: newValue.lastName,
          athleteName: `${newValue.firstName} ${newValue.lastName}`,
          athleteNumber: newValue.clubAthleteNumber,
        };
      }
      return { ...row, [columnKey]: newValue };
    }

    const updatedRow = {
      ...row,
      [columnKey]: !type ? newValue : { ...row[columnKey], [type]: newValue },
    };

    // Handle field event attempt auto-correction
    if (
      columnKey.startsWith('attempt') &&
      (type === 'distance' || type === 'status')
    ) {
      const currentAttempt = updatedRow[columnKey];
      if (
        currentAttempt &&
        currentAttempt.distance !== undefined &&
        currentAttempt.status !== undefined
      ) {
        const numDistance = Number(currentAttempt.distance);
        const status = currentAttempt.status;

        // Auto-correction logic
        if (type === 'distance' && numDistance > 0 && status !== 'OK') {
          updatedRow[columnKey] = {
            ...currentAttempt,
            distance: numDistance,
            status: 'OK',
          };
          toast.info(
            `Auto-corrected status to OK for distance ${numDistance}m`
          );
        } else if (
          type === 'status' &&
          (status === 'Foul' || status === 'NR') &&
          numDistance > 0
        ) {
          updatedRow[columnKey] = {
            ...currentAttempt,
            distance: 0,
            status: status,
          };
          toast.info(`Auto-corrected distance to 0 for ${status} status`);
        } else if (
          type === 'distance' &&
          numDistance === 0 &&
          status === 'OK'
        ) {
          updatedRow[columnKey] = {
            ...currentAttempt,
            distance: 0,
            status: 'NR',
          };
          toast.info(`Auto-corrected status to NR for distance 0`);
        } else {
          const validation = validateFieldEventAttempt(
            currentAttempt.distance,
            currentAttempt.status
          );
          if (!validation.isValid) {
            toast.error(`Attempt: ${validation.error}`);
          }
        }
      }
    }

    return updatedRow;
  };

  // TRACK EVENT HANDLER (Sprint, Distance, etc.)
  const handleTrackEventValueChange = (row, columnKey, newValue, type) => {
    // Handle athlete selection for new rows
    if (row.isNew && columnKey === 'athleteName') {
      if (typeof newValue === 'object' && newValue.athleteID) {
        return {
          ...row,
          athleteID: newValue.athleteID,
          athleteAge: newValue.ageGroup,
          athleteGender: newValue.gender,
          athleteFirstName: newValue.firstName,
          athleteLastName: newValue.lastName,
          athleteName: `${newValue.firstName} ${newValue.lastName}`,
          athleteNumber: newValue.clubAthleteNumber,
        };
      }
      return { ...row, [columnKey]: newValue };
    }

    const updatedRow = {
      ...row,
      [columnKey]: !type ? newValue : { ...row[columnKey], [type]: newValue },
    };

    // Handle track event validation and auto-correction
    if (columnKey === 'resultValue' || columnKey === 'status') {
      const currentResultValue = updatedRow.resultValue;
      const currentStatus = updatedRow.status;

      if (currentResultValue !== undefined && currentStatus !== undefined) {
        const validation = validateTrackEventResult(
          currentResultValue,
          currentStatus
        );

        if (columnKey === 'status') {
          if (!validation.isValid) {
            toast.error(`Track Result: ${validation.error}`);
          } else if (
            validation.shouldAutoCorrect &&
            validation.correctedStatus === 'DNF'
          ) {
            updatedRow.status = 'DNF';
            toast.info(`Auto-corrected status to DNF for empty time`);
          }
        } else if (columnKey === 'resultValue') {
          if (
            validation.shouldAutoCorrect &&
            validation.correctedStatus === 'OK'
          ) {
            updatedRow.status = 'OK';
          }
        }
      }
    }

    return updatedRow;
  };

  const addResult = async () => {
    // Add mode check
    if (!EDITING_MODES[editingMode].canAddAthlete) {
      toast.warning(EDITING_MODES[editingMode].message);
      return;
    }

    // A relay's rows are teams: no athlete is picked, so the meet's athletes
    // are not asked for.
    if (!isRelay && athletes.length === 0) {
      await fetchAthletes();
    }

    let newTableData = [];
    tableData.forEach((td) => newTableData.push(td));
    const newRow = getNewColumnTempData(results, eventType);
    newTableData.push(
      isRelay
        ? { ...newRow, isTeam: true, teamID: null, teamChoice: null, typedTeamName: '' }
        : newRow
    );
    setTableData(newTableData);
  };

  const addFieldEventAttempt = () => {
    // Add mode check
    if (!EDITING_MODES[editingMode].canAddAttempt) {
      toast.warning(EDITING_MODES[editingMode].message);
      return;
    }

    // Keep all original logic exactly the same
    const newTableData = getFieldEventResultsWithNewAttempt(
      tableData,
      eventType
    );
    setTableData(newTableData);
  };

  const openResultDeleteConfirmPopup = (item) => {
    if (item.isNew) {
      setTableData((prevData) =>
        prevData.filter((d) => d.resultID !== item.resultID)
      );
      return; // No confirm needed for new athletes
    }

    // ✅ Reuse your existing functions
    let hasUnsavedChanges = false;

    if (fieldEvents.includes(eventType) && eventType !== 'High Jump') {
      hasUnsavedChanges = hasUnsavedFieldEventChanges();
    } else if (eventType === 'High Jump') {
      hasUnsavedChanges = hasUnsavedHighJumpChanges();
    } else {
      hasUnsavedChanges = hasUnsavedTrackEventChanges();
    }

    if (hasUnsavedChanges) {
      toast.warning('Please save all changes before deleting an athlete.');
      return;
    }

    setDeleteConfirmPopup(deleteResultPopupUi(organisationWordOf(club)));
    setShowConfirm(true);
    setConfirmingResult(item);
  };

  useEffect(() => {
    const transformedTableData = getTableData(
      results,
      eventType,
      styles.delete
    );
    setTableData(transformedTableData);

    // For high jump, we need to preserve the original structure
    if (eventType === 'High Jump') {
      setOriginalTableData(results); // Keep the original API structure
    } else {
      const processedOriginalData = results.map((item) => {
        return {
          ...item,
          resultValue: convertMillisecondsToRecord(item.resultRawValue),
        };
      });
      setOriginalTableData(processedOriginalData);
    }
  }, [results, eventType, showPopup]);

  // handles deleting an attempt or height column
  const openColumnDeleteConfirmPopup = () => {
    if (fieldEvents.includes(eventType) && eventType !== 'High Jump') {
      // Field events (excluding high jump)
      if (tableData[0]?.attempts?.length <= 1) {
        toast.error('Cannot delete the first attempt.');
        return;
      }

      const lastAttemptIndex = tableData[0].attempts.length;
      const attemptToDelete = tableData[0][`attempt${lastAttemptIndex}`];

      // ✅ Handle new attempts - delete directly without confirmation
      if (attemptToDelete?.isNew) {
        const newTableData = tableData.map((res) => {
          const updatedResult = { ...res };
          delete updatedResult[`attempt${res.attempts.length}`];
          updatedResult.attempts = [...res.attempts];
          updatedResult.attempts.pop();
          return updatedResult;
        });
        setTableData(newTableData);
        return; // ✅ STOP HERE - don't show confirmation
      }

      // For existing attempts, check for unsaved changes
      if (hasUnsavedFieldEventChanges()) {
        toast.warning('Please save all changes before deleting an attempt.');
        return; // ✅ STOP HERE - don't show confirmation
      }

      // ✅ Only reach here for existing attempts with no unsaved changes
      setDeleteConfirmPopup(deleteConfirmPopupUi.attempt);
      setShowConfirm(true);
    } else if (eventType === 'High Jump') {
      // High jump events
      if (tableData[0]?.heightAttempts?.length <= 1) {
        toast.error('Cannot delete the first height.');
        return;
      }

      const lastHeightAttempt =
        tableData[0].heightAttempts[tableData[0].heightAttempts.length - 1];

      // ✅ Handle new heights - delete directly without confirmation
      if (lastHeightAttempt?.isNew) {
        const newTableData = tableData.map((result) => {
          const updatedHeightAttempts = [...result.heightAttempts];
          updatedHeightAttempts.pop();
          const updatedHighJumpAttempts = [...result.highJumpAttempts];
          updatedHighJumpAttempts.pop();
          const updatedResult = { ...result };
          delete updatedResult[
            `heightAttempts${result.heightAttempts.length - 1}`
          ];
          return {
            ...updatedResult,
            heightAttempts: updatedHeightAttempts,
            highJumpAttempts: updatedHighJumpAttempts,
          };
        });
        setTableData(newTableData);
        return; // ✅ STOP HERE - don't show confirmation
      }

      // For existing heights, check for unsaved changes
      if (hasUnsavedHighJumpChanges()) {
        toast.warning('Please save all changes before deleting a height.');
        return; // ✅ STOP HERE - don't show confirmation
      }

      // ✅ Only reach here for existing heights with no unsaved changes
      setDeleteConfirmPopup(deleteConfirmPopupUi.height);
      setShowConfirm(true);
    }
  };

  const closeConfirmPopup = () => {
    if (isDeleting) {
      return; // Don't close while deleting
    }
    setShowConfirm(false);
    setConfirmingResult(null);
  };

  const handleNewResultDelete = (item) => {
    const newTableData = tableData.filter(
      (res) => res.resultID !== item.resultID
    );
    setTableData(newTableData);
  };

  const handleDeleteResult = async () => {
    try {
      setIsDeleting(true);

      let resultIds = [];
      if (fieldEvents.includes(eventType)) {
        for (let i = 0; i < confirmingResult.attempts.length; i++) {
          resultIds.push(confirmingResult[`attempt${i + 1}`].resultID);
        }
      } else if (eventType === 'High Jump') {
        resultIds = confirmingResult.heightAttempts
          .map((at) => at.resultId)
          .filter((id) => !id.includes('new'));
      } else {
        resultIds = [confirmingResult.resultID];
      }

      // Call the delete API (note: only needs resultID and clubID, not meetID)
      const response = await deleteResults(resultIds, clubID);
      // Display the server's response message
      if (response?.data?.Message) {
        toast.success(response.data.Message);
      } else {
        toast.success('Result deleted successfully.');
      }

      // Close the confirm popup
      setShowConfirm(false);
      setConfirmingResult(null);

      // Refresh the results to update the UI
      if (refreshResults) {
        await refreshResults();
      }
    } catch (err) {
      console.error('[deleteResult] error:', err);

      // Handle specific error responses from the server
      let errorMessage = 'Failed to delete result';

      if (err?.response?.data?.Message) {
        // Use the server's error message
        errorMessage = err.response.data.Message;
      } else if (err?.response?.data?.Error) {
        // Use the server's error details
        errorMessage = err.response.data.Error;
      } else if (err?.message) {
        // Fallback to the general error message
        errorMessage = err.message;
      }

      // Handle specific error cases based on the API documentation
      if (errorMessage.includes('approved club records')) {
        toast.error(
          `Cannot delete this result: It is an approved ${organisationWordOf(club)} record.`
        );
      } else if (errorMessage.includes('Results not found')) {
        toast.error('Result not found or already deleted.');
      } else if (errorMessage.includes('not authenticated')) {
        toast.error('You are not authenticated to perform this action.');
      } else if (errorMessage.includes('Forbid')) {
        toast.error(
          'You do not have permission to delete results in this club.'
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const saveResults = async () => {
    try {
      if (!tableData?.length) {
        return false;
      }
      // Each save function answers 'no-changes' when it had nothing to send.
      // That answer is passed on, so Save can say so and skip the reload.
      let outcome;
      if (fieldEvents.includes(eventType)) {
        outcome = await saveFieldEventResults(
          clubID,
          meetID,
          eventID,
          tableData,
          originalTableData
        );
      } else if (eventType === 'High Jump') {
        // 1) get only the rows that have heights
        const jumpRows = tableData.filter(
          (row) =>
            Array.isArray(row.heightAttempts) && row.heightAttempts.length > 0
        );

        // 2) check for any invalid height
        const hasInvalidHeight = jumpRows.some((row) =>
          row.heightAttempts.some((attempt, idx, arr) => {
            const h = attempt.height;
            // rule A: out of overall range
            if (isNaN(h) || h < 0.5 || h > 2.5) return true;
            // rule B: not strictly above previous
            const prev = arr[idx - 1];
            if (prev && h <= prev.height) return true;
            // rule C: not strictly below next
            const next = arr[idx + 1];
            if (next && h >= next.height) return true;
            return false;
          })
        );

        // 3) if any invalid → block save
        if (hasInvalidHeight) {
          toast.error(
            'Cannot save: one or more jump heights are invalid (must be 0.50–2.50 m, strictly increasing).',
            { toastId: 'save-highjump-error' }
          );
          return; // <<< abort
        }

        // 4) otherwise, proceed with your existing save
        outcome = await saveHighJumpEventResults(
          clubID,
          meetID,
          eventID,
          tableData,
          originalTableData
        );
      } else {
        outcome = await saveTrackEventResults(
          clubID,
          meetID,
          eventID,
          eventType,
          tableData,
          originalTableData
        );
      }
      return outcome === 'no-changes' ? 'no-changes' : true;
    } catch (err) {
      console.log('[saveResults] error response: ', err);
      toast.error(getErrorDetails(err, 'Results Update Failed'));
      return false;
    }
  };

  const finaliseResults = async () => {
    try {
      setIsUpdating(true);
      const response = await saveResults();
      if (response === 'no-changes') {
        toast.info('No changes detected, results not updated.');
        return;
      }
      if (!response) return;
      toast.success('Results updated successfully.');
      // Stay in the dialog after a save, so several changes can be made one
      // after another without going back to the event page each time; Back
      // leaves it. The saved results are loaded first, and Save stays
      // disabled until they arrive (isUpdating is cleared in finally), so the
      // same change cannot be saved twice. The rows are rebuilt from the
      // fresh results by the effect that watches the results prop.
      if (refreshResults) {
        await refreshResults();
      } else {
        closePopup();
      }
    } catch (err) {
      console.log('[ResultEditPopup] error response: ', err);
      toast.error(getErrorDetails(err, 'Results Update Failed'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFieldEventAttemptDelete = async () => {
    const attempts = getfieldEventResultFinalAttemptsForDelete(tableData);
    if (!attempts?.length) {
      return false;
    }
    if (attempts[0].isNew) {
      // If it's new temp attemps, remove the temp data on the UI
      const newTableData = tableData.map((res) => {
        res[`attempt${res.attempts.length}`] = undefined;
        res.attempts.pop();
        return res;
      });
      setTableData(newTableData);
      setShowConfirm(false);
      return false;
    }
    try {
      setIsDeleting(true);
      // Call the delete API (note: only needs resultID and clubID, not meetID)
      const response = await deleteResults(
        attempts.map((at) => at.resultID),
        clubID
      );

      // Display the server's response message
      if (response?.data?.Message) {
        toast.success(response.data.Message);
      } else {
        toast.success('Attempt deleted successfully.');
      }
      // Load the results as they now are (the confirm popup closes in finally)
      if (refreshResults) {
        await refreshResults();
      }
    } catch (err) {
      console.log('[ResultEditPopup] error response: ', err);
      toast.error(getErrorDetails(err, 'Results Update Failed'));
    } finally {
      setShowConfirm(false);
      setConfirmingResult(null);
      setIsDeleting(false);
    }
  };

  /*******
   * High Jump operations addHighJumpResultHeight / handleHighJumpHeightDelete
   */
  const addHighJumpResultHeight = () => {
    // Add mode check
    if (!EDITING_MODES[editingMode].canAddHeight) {
      toast.warning(EDITING_MODES[editingMode].message);
      return;
    }

    // Keep all original logic exactly the same
    const newTableData = getHighJumpEventResultsWithNewAttempt(
      tableData,
      eventType
    );
    setTableData(newTableData);
  };

  const handleHighJumpHeightDelete = async () => {
    if (!tableData?.length || !tableData[0]?.heightAttempts?.length) {
      return false;
    }

    const lastHeightIndex = tableData[0].heightAttempts.length - 1;
    const lastHeightAttempt = tableData[0].heightAttempts[lastHeightIndex];

    // If it's a new height (not saved to server yet), just remove it from UI
    if (lastHeightAttempt?.isNew) {
      const newTableData = tableData.map((result) => {
        // Remove the last height attempt from heightAttempts array
        const updatedHeightAttempts = [...result.heightAttempts];
        updatedHeightAttempts.pop();

        // Remove the last highJumpAttempts UI element
        const updatedHighJumpAttempts = [...result.highJumpAttempts];
        updatedHighJumpAttempts.pop();

        // Remove the heightAttempts{index} property
        const updatedResult = { ...result };
        delete updatedResult[
          `heightAttempts${result.heightAttempts.length - 1}`
        ];

        return {
          ...updatedResult,
          heightAttempts: updatedHeightAttempts,
          highJumpAttempts: updatedHighJumpAttempts,
        };
      });

      setTableData(newTableData);
      setShowConfirm(false);
      return true;
    }

    // If it's an existing height, delete from server
    try {
      setIsDeleting(true);

      // Collect all result IDs for the last height from all athletes
      const resultIdsToDelete = tableData
        .map((result) => {
          const lastAttempt = result.heightAttempts[lastHeightIndex];
          return lastAttempt?.resultId;
        })
        .filter((id) => id && !id.includes('new')); // Only real IDs, not temp ones

      if (resultIdsToDelete.length > 0) {
        // Call the delete API
        const response = await deleteResults(resultIdsToDelete, clubID);

        // Display success message
        if (response?.data?.Message) {
          toast.success(response.data.Message);
        } else {
          toast.success('Height deleted successfully.');
        }

        // Refresh the results to update the UI
        if (refreshResults) {
          await refreshResults();
        }
      } else {
        // No real results to delete, just update UI
        toast.info('Height removed from display.');
      }
    } catch (err) {
      console.error('[handleHighJumpHeightDelete] error:', err);

      let errorMessage = 'Failed to delete height';
      if (err?.response?.data?.Message) {
        errorMessage = err.response.data.Message;
      } else if (err?.response?.data?.Error) {
        errorMessage = err.response.data.Error;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
      setShowConfirm(false);
    }
  };

  const updateEditingMode = () => {
    // Check for new athletes first
    const hasNewAthlete = tableData.some((row) => row.isNew);
    if (hasNewAthlete) {
      setEditingMode('ADD_ATHLETE');
      return;
    }

    // Check for new attempts (field events)
    if (fieldEvents.includes(eventType) && eventType !== 'High Jump') {
      const hasNewAttempt = tableData.some((result) => {
        for (let i = 1; i <= ATTEMPT_MAX_COUNT; i++) {
          if (result[`attempt${i}`]?.isNew) return true;
        }
        return false;
      });
      if (hasNewAttempt) {
        setEditingMode('ADD_ATTEMPT');
        return;
      }
    }

    // Check for new heights (high jump)
    if (eventType === 'High Jump') {
      const hasNewHeight = tableData.some((result) => {
        return result.heightAttempts?.some((attempt) => attempt.isNew);
      });
      if (hasNewHeight) {
        setEditingMode('ADD_HEIGHT');
        return;
      }
    }

    // Check for existing edits using your existing functions (NO DUPLICATION)
    let hasUnsavedChanges = false;
    if (fieldEvents.includes(eventType) && eventType !== 'High Jump') {
      hasUnsavedChanges = hasUnsavedFieldEventChanges();
    } else if (eventType === 'High Jump') {
      hasUnsavedChanges = hasUnsavedHighJumpChanges();
    } else {
      hasUnsavedChanges = hasUnsavedTrackEventChanges();
    }

    setEditingMode(hasUnsavedChanges ? 'EDIT_EXISTING' : 'VIEW');
  };

  // 3. USE this clean useEffect with ESLint disable comment:
  useEffect(() => {
    updateEditingMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, eventType]);

  const getTooltipMessage = (buttonType) => {
    if (editingMode === 'VIEW') return null;

    const messages = {
      ADD_ATHLETE: 'Save the new athlete first',
      ADD_ATTEMPT: 'Save the new attempt first',
      ADD_HEIGHT: 'Save the new height first',
      EDIT_EXISTING: 'Save your changes first',
    };

    const message = messages[editingMode];

    // Only show tooltip if the button is disabled
    if (buttonType === 'athlete' && !EDITING_MODES[editingMode].canAddAthlete) {
      return message;
    }
    if (buttonType === 'attempt' && !EDITING_MODES[editingMode].canAddAttempt) {
      return message;
    }
    if (buttonType === 'height' && !EDITING_MODES[editingMode].canAddHeight) {
      return message;
    }

    return null;
  };

  // Render the popup

  return (
    <div
      className={`${styles.resultEditPopupOverlay} ${
        showPopup ? styles.active : ''
      }`}
    >
      <div className={styles.resultEditPopup}>
        <div className={styles.resultEditPopupContainer}>
          <div className={styles.title}>{title}</div>
          <div className={styles.subtitle}>{subtitle}</div>
          <div className={styles.buttonWrapper}>
            {preliminary && (
              <div className={styles.preliminary}>Preliminary</div>
            )}
              {roundType && (
            <div className={styles.roundTypeDisplay}>
              {roundType}
            </div>
          )}
            <div style={{ display: 'flex' }}>
              <CustomNewButton
                onClick={() => addResult()}
                label={isRelay ? '+ Add Team' : '+ Add Athlete'}
                variant="primary2"
                disabled={!EDITING_MODES[editingMode].canAddAthlete}
                tooltip={getTooltipMessage('athlete')}
              />
              {isFieldEvent && (
                <CustomNewButton
                  onClick={() => addFieldEventAttempt()}
                  label="+ Add Attempt"
                  style={{ marginLeft: 10 }}
                  disabled={
                    tableData?.length === 0 ||
                    tableData[0].attempts?.length === ATTEMPT_MAX_COUNT ||
                    !EDITING_MODES[editingMode].canAddAttempt
                  }
                  tooltip={getTooltipMessage('attempt')}
                />
              )}
              {eventType === 'High Jump' && (
                <CustomNewButton
                  onClick={() => addHighJumpResultHeight()}
                  label="+ Add Height"
                  style={{ marginLeft: 10 }}
                  disabled={
                    tableData?.length === 0 ||
                    !EDITING_MODES[editingMode].canAddHeight
                  }
                  tooltip={getTooltipMessage('height')}
                />
              )}
            </div>
          </div>
          <div className={styles.table}>
            <div className={styles.tableWrapper}>
              {tableData?.length > 0 ? (
                <AdminDataTable
                  isEditable={true}
                  borderType="full"
                  isAddedFeatures={false}
                  columns={editColumns}
                  primaryField={editColumnPrimaryField}
                  data={tableData}
                  handleValueChange={handleValueChange}
                  handleColDelete={openColumnDeleteConfirmPopup}
                  handleRowDelete={handleNewResultDelete}
                  openConfirmPopup={openResultDeleteConfirmPopup}
                  searchInputPlaceholder=""
                  // A relay: the team, result, lane and status. A field event:
                  // ID, Name, Gender and Age, then the attempts or heights.
                  headStyle={{
                    gridTemplateColumns: isRelay
                      ? '5fr 3fr 3fr 3fr' // a relay: team, result, lane, status
                      : isFieldEvent
                      ? `1fr 3fr 2fr 2fr ${'3fr '.repeat(tableData[0]?.attempts?.length || 0).trim()}`
                      : isHighJump
                        ? `1fr 3fr 2fr 2fr ${'3fr '.repeat(tableData[0]?.highJumpAttempts?.length || 0).trim()}`
                        : '1fr 4fr 2fr 2fr 3fr 3fr 3fr', // Track events (removed PB column)
                    background: '#f1f1f2',
                  }}
                  rowStyle={{
                    gridTemplateColumns: isRelay
                      ? '5fr 3fr 3fr 3fr' // a relay: team, result, lane, status
                      : isFieldEvent
                      ? `1fr 3fr 2fr 2fr ${'3fr '.repeat(tableData[0]?.attempts?.length || 0).trim()}`
                      : isHighJump
                        ? `1fr 3fr 2fr 2fr ${'3fr '.repeat(tableData[0]?.highJumpAttempts?.length || 0).trim()}`
                        : '1fr 4fr 2fr 2fr 3fr 3fr 3fr', // Track events (removed PB column)
                  }}
                />
              ) : (
                <div className={styles.noData}>No results available</div>
              )}
            </div>
          </div>
          {/* <div className={styles.popupText}>
            You can publish these results immediately, or save them to the
            &#34;Pending&#34; tab.
          </div> */}
          <div className={styles.replayNote}>
            <ReplayDelayNote organisationWord={organisationWordOf(club)} />
          </div>
          <div className={styles.buttons}>
            <CustomNewButton
              label="Save"
              onClick={() => finaliseResults()}
              disabled={isUpdating}
              large={true}
            ></CustomNewButton>
          </div>
          <div
            className={styles.goBack}
            onClick={() => {
              closePopup();
            }}
          >
            <div className={styles.goBackImage}>
              <img src={BackIcon} alt="back" />
            </div>
            <div className={styles.goBackText}>Back</div>
          </div>
        </div>
        <div
          className={styles.closeBtn}
          onClick={() => {
            closePopup();
          }}
        >
          <img src={CloseIcon} alt="close" />
        </div>
      </div>
      <CustomNewConfirm
        isOpen={showConfirm}
        onClose={closeConfirmPopup}
        title={deleteConfirmPopup.title}
        onConfirm={
          deleteConfirmPopup.key === 'result'
            ? handleDeleteResult
            : deleteConfirmPopup.key === 'attempt'
              ? handleFieldEventAttemptDelete
              : handleHighJumpHeightDelete
        }
        description={deleteConfirmPopup.description}
        warningText={deleteConfirmPopup.warningText}
        showWarning={true}
        isLoading={isDeleting}
        confirmButtonText={isDeleting ? 'Deleting...' : 'Delete'}
      ></CustomNewConfirm>
      <RelayRunnersPopup
        isOpen={!!runnersFor}
        teamName={runnersFor?.athleteFirstName || ''}
        legCount={relayLegCount(eventType)}
        runners={runnersFor?.runners}
        athletes={athletes}
        groupingBasis={groupingBasis}
        isSaving={isSavingRunners}
        onSave={saveRunners}
        onClose={() => !isSavingRunners && setRunnersFor(null)}
      />
    </div>
  );
};

ResultEditPopup.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  resultType: PropTypes.string,
  results: PropTypes.arrayOf(PropTypes.object),
  clubID: PropTypes.string,
  meetID: PropTypes.string,
  eventID: PropTypes.string,
  viewType: PropTypes.string,
  eventType: PropTypes.string,
  ageGroup: PropTypes.string,
  gender: PropTypes.string,
  roundType: PropTypes.string,
  showPopup: PropTypes.bool,
  closePopup: PropTypes.func,
  refreshResults: PropTypes.func,
  // A relay team's runners were saved: (resultID, runners as saved).
  onRunnersSaved: PropTypes.func,
  preliminary: PropTypes.bool,
};

export default ResultEditPopup;
